import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { X, AlertTriangle, CheckCircle2, XCircle, KeyRound, Copy, ShieldAlert } from 'lucide-react';
import { proxyZugang, type ProxyZugangErgebnis } from '../api';

/**
 * Den Zugang zum Proxy-Panel eines Servers setzen.
 *
 * ══ WARUM DAS EIN EIGENER DIALOG IST ═══════════════════════════════════════
 *
 * Weil es drei Stellen auf einmal ändert (Proxy, PC, Sync auf dem Host) und
 * jede davon einzeln misslingen kann. Ein Knopf, der nur „gespeichert" sagt,
 * verschweigt genau den Fall, der weh tut: Kennwort im Proxy geändert, Sync
 * nicht nachgezogen — ab da entstehen keine Proxy-Einträge mehr, und das
 * fällt erst beim nächsten Ausrollen auf.
 *
 * Deshalb zeigt der Dialog jeden Schritt einzeln.
 *
 * ══ DAS KENNWORT STEHT GENAU EINMAL DA ═════════════════════════════════════
 *
 * Ein erzeugtes Kennwort wird einmal angezeigt und danach nie wieder — es
 * liegt verschlüsselt im PC, und wer es braucht, setzt ein neues. Eines, das
 * man beliebig oft abrufen kann, ist keines.
 */

export default function ProxyZugangModal({
  serverId,
  hostname,
  onClose,
}: {
  serverId: string;
  hostname: string;
  onClose: () => void;
}) {
  const [eigenes, setEigenes] = useState(false);
  const [kennwort, setKennwort] = useState('');
  const [ergebnis, setErgebnis] = useState<ProxyZugangErgebnis | null>(null);
  const [kopiert, setKopiert] = useState(false);

  const zustand = useQuery({
    queryKey: ['proxy-zugang', serverId],
    queryFn: () => proxyZugang.lesen(serverId),
  });

  const setzen = useMutation({
    mutationFn: () => proxyZugang.setzen(serverId, eigenes ? kennwort : undefined),
    onSuccess: (e) => {
      setErgebnis(e);
      zustand.refetch();
    },
  });

  const z = zustand.data;
  const zuKurz = eigenes && kennwort.length > 0 && kennwort.length < 8;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-xl rounded-xl bg-surface p-5 shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-1.5 text-base font-semibold text-slate-900">
              <KeyRound size={16} /> Zugang zum Proxy-Panel
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">{hostname}</p>
          </div>
          <button onClick={onClose} className="rounded p-1 text-slate-400 hover:bg-slate-100">
            <X size={16} />
          </button>
        </div>

        {zustand.isLoading && <p className="mt-4 text-sm text-slate-500">Wird gelesen …</p>}

        {z && !z.vorhanden && (
          <p className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            {z.hinweis}
          </p>
        )}

        {z?.vorhanden && (
          <>
            {/* Ist-Zustand */}
            <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-xs">
              <dt className="text-slate-500">Adresse im Panel</dt>
              <dd className="font-mono text-slate-800">{z.email ?? '—'}</dd>
              <dt className="text-slate-500">Hauptadmin im PC</dt>
              <dd className="text-slate-800">
                {z.hauptadmin ? (
                  <>
                    {z.hauptadmin.name}{' '}
                    <span className="font-mono text-slate-500">{z.hauptadmin.email}</span>
                  </>
                ) : (
                  <span className="text-rose-700">keiner eingetragen</span>
                )}
              </dd>
              <dt className="text-slate-500">Stimmt überein</dt>
              <dd>
                {z.passtZumHauptadmin ? (
                  <span className="inline-flex items-center gap-1 text-emerald-700">
                    <CheckCircle2 size={12} /> ja
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-amber-700">
                    <AlertTriangle size={12} /> nein
                  </span>
                )}
              </dd>
            </dl>

            {z.auslieferungszustand && (
              <div className="mt-3 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
                <ShieldAlert size={14} className="mt-px shrink-0" />
                <span>{z.hinweis}</span>
              </div>
            )}

            {/* Kennwort */}
            <div className="mt-4 rounded-lg border border-slate-200 p-3">
              <label className="flex items-center gap-2 text-xs text-slate-700">
                <input
                  type="radio"
                  checked={!eigenes}
                  onChange={() => setEigenes(false)}
                />
                Kennwort erzeugen lassen
                <span className="text-slate-400">— wird einmal angezeigt</span>
              </label>
              <label className="mt-2 flex items-center gap-2 text-xs text-slate-700">
                <input type="radio" checked={eigenes} onChange={() => setEigenes(true)} />
                Eigenes setzen
              </label>
              {eigenes && (
                <input
                  type="password"
                  value={kennwort}
                  onChange={(e) => setKennwort(e.target.value)}
                  placeholder="mindestens 8 Zeichen"
                  className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              )}
              {zuKurz && <p className="mt-1 text-[11px] text-rose-600">Zu kurz.</p>}
            </div>

            <p className="mt-3 text-[11px] leading-relaxed text-slate-500">
              Gesetzt wird an drei Stellen gleichzeitig: im Proxy selbst, im PC und in der
              Umgebung des Sync-Containers auf dem Host. Wird nur eine davon geändert, legt das
              den Sync still — dann entstehen für neue Instanzen keine Proxy-Einträge mehr.
            </p>

            {/* Ergebnis */}
            {ergebnis && (
              <div className="mt-4 space-y-2">
                <div
                  className={`rounded-lg border px-3 py-2 text-xs ${
                    ergebnis.ok
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                      : 'border-rose-200 bg-rose-50 text-rose-800'
                  }`}
                >
                  {ergebnis.meldung}
                </div>

                <ul className="space-y-1">
                  {ergebnis.schritte.map((s) => (
                    <li key={s.ort} className="flex items-center gap-2 text-[11px]">
                      {s.ok ? (
                        <CheckCircle2 size={11} className="text-emerald-600" />
                      ) : (
                        <XCircle size={11} className="text-rose-600" />
                      )}
                      <span className="font-medium text-slate-700">{s.ort}</span>
                      <span className="text-slate-500">{s.meldung}</span>
                    </li>
                  ))}
                </ul>

                {ergebnis.kennwort && (
                  <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
                    <div className="text-[11px] font-medium text-amber-900">
                      Einmalige Anzeige — jetzt notieren:
                    </div>
                    <div className="mt-1 flex items-center gap-2">
                      <code className="flex-1 break-all rounded bg-white px-2 py-1 font-mono text-xs text-slate-800">
                        {ergebnis.kennwort}
                      </code>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(ergebnis.kennwort!);
                          setKopiert(true);
                          window.setTimeout(() => setKopiert(false), 2000);
                        }}
                        className="inline-flex items-center gap-1 rounded border border-amber-300 px-2 py-1 text-[10px] font-medium text-amber-900 hover:bg-amber-100"
                      >
                        <Copy size={10} /> {kopiert ? 'kopiert' : 'kopieren'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {setzen.isError && (
              <p className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
                {(setzen.error as Error).message}
              </p>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={onClose}
                className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
              >
                Schliessen
              </button>
              <button
                onClick={() => setzen.mutate()}
                disabled={setzen.isPending || zuKurz || (eigenes && kennwort.length < 8) || !z.hauptadmin}
                className="rounded-lg bg-brand-600 px-3 py-2 text-xs font-medium text-white disabled:opacity-40"
              >
                {setzen.isPending ? 'wird gesetzt …' : 'Zugang setzen'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
